// frontend/src/components/WeeklyCalendarModal.js

import React, { useEffect, useMemo, useState } from "react";
import { Star, CaretLeft, CaretRight, X } from "phosphor-react";
import ActivityLightbox from "./ActivityLightbox";

/**
 * Props:
 * - open, onClose (existing)
 * - weekStartDate?: Date (optional – defaults to current week, Monday start)
 * - activitiesByDay: { 'YYYY-MM-DD': Activity[] }
 * - currentUser: viewer user object
 */
export default function WeeklyCalendarModal({
  open,
  onClose,
  weekStartDate,
  activitiesByDay = {},
  currentUser,
}) {
  const [lb, setLb] = useState({ open: false, activity: null, id: null });

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const check = () =>
      setIsMobile(window.matchMedia("(max-width: 767px)").matches);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const getMonday = (dateLike) => {
    const base = new Date(dateLike || new Date());
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    const day = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - day);
    return d;
  };

  const [weekOffset, setWeekOffset] = useState(0);

  const anchorMonday = useMemo(
    () => getMonday(weekStartDate || new Date()),
    [weekStartDate]
  );

  const start = useMemo(() => {
    const d = new Date(anchorMonday);
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [anchorMonday, weekOffset]);

  const toYMD = (dt) =>
    `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(
      dt.getDate()
    ).padStart(2, "0")}`;

  const days = useMemo(() => {
    const arr = [];

    for (let i = 0; i < 7; i += 1) {
      const dt = new Date(start);
      dt.setDate(start.getDate() + i);

      const ymd = toYMD(dt);

      const prev = new Date(dt);
      prev.setDate(dt.getDate() - 1);
      const prevYmd = toYMD(prev);

      const stars = (activitiesByDay[prevYmd] || [])
        .slice()
        .sort(
          (a, b) =>
            new Date(a.timestamp || a.created_at) -
            new Date(b.timestamp || b.created_at)
        );

      arr.push({
        label: dt.toLocaleString("en-US", { weekday: "short" }).toUpperCase(),
        dateLabel: `${dt.getMonth() + 1}/${dt.getDate()}`,
        ymd,
        stars,
      });
    }

    return arr;
  }, [activitiesByDay, start]);

  if (!open) return null;

  const prevDisabled = weekOffset <= -1;
  const nextDisabled = weekOffset >= 0;

  return (
    <div className="fixed inset-0 z-[150] grid place-items-center bg-black/35 overflow-y-auto p-0 md:p-6">
      <div
        className="
          relative w-[94vw] max-w-4xl overflow-hidden
          rounded-[28px] border border-blue-100/80
          bg-blue-50
          p-4 md:p-6 shadow-[0_8px_0px_rgba(37,99,235,0.96)]
        "
      >
        <button
          onClick={onClose}
          className="
            absolute right-4 top-4 z-10 grid h-9 w-9 place-items-center
            rounded-full bg-white/80 text-blue-500 hover:bg-blue-200
          "
          aria-label="Close"
        >
          <X size={19} weight="bold" />
        </button>

        {/* Title + week navigation */}
        <div className="text-center">
          <div className="mt-1 flex items-center justify-center">
            <button
              type="button"
              onClick={() =>
                !prevDisabled && setWeekOffset((o) => Math.max(-1, o - 1))
              }
              disabled={prevDisabled}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white disabled:cursor-not-allowed"
              aria-label="Previous week"
              title="Previous week"
            >
              <CaretLeft
                size={25}
                weight="fill"
                className={prevDisabled ? "text-blue-300" : "text-blue-400"}
              />
            </button>

            <h2 className="mx-2 text-blue-600 font-black leading-none tracking-[-0.04em] text-[32px]">
              {weekOffset === 0 ? "This Week" : "Last Week"}
            </h2>

            <button
              type="button"
              onClick={() =>
                !nextDisabled && setWeekOffset((o) => Math.min(0, o + 1))
              }
              disabled={nextDisabled}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition hover:bg-white disabled:cursor-not-allowed"
              aria-label="Next week"
              title="Next week"
            >
              <CaretRight
                size={25}
                weight="fill"
                className={nextDisabled ? "text-blue-300" : "text-blue-400"}
              />
            </button>
          </div>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 outfit-font gap-1.5 md:gap-2.5 mt-5 mb-1 text-blue-600 font-semibold">
          {days.map((d) => (
            <div key={d.ymd} className="pl-2 text-sm">
              {d.label} <span className="text-blue-400">{d.dateLabel}</span>
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-7 gap-1.5 md:gap-2.5">
          {days.map((d) => {
            const maxStars = isMobile ? 1 : 8;
            const extra = Math.max(0, (d.stars?.length || 0) - maxStars);
            const starsToShow = (d.stars || []).slice(0, maxStars);
            const dense = (d.stars?.length || 0) > (isMobile ? 3 : 4);
            const starSize = isMobile ? 18 : dense ? 14 : 16;

            return (
              <div
                key={d.ymd}
                className="
                  group relative min-h-[74px] md:min-h-[120px]
                  overflow-hidden rounded-[22px]
                  border border-blue-100/80 bg-white/90
                  p-1.5 md:p-2
                  shadow-[0_10px_30px_rgba(37,99,235,0.06)]
                  transition
                "
              >
                {(starsToShow.length > 0 || extra > 0) && (
                  <div
                    className={
                      isMobile
                        ? "mt-1 flex items-center justify-center"
                        : "mt-1 grid grid-cols-4 auto-rows-max gap-1 pr-1"
                    }
                  >
                    {starsToShow.map((a) => (
                      <button
                        key={a.id}
                        title="View activity"
                        onClick={() =>
                          setLb({ open: true, activity: null, id: a.id })
                        }
                        className="p-0.5 rounded active:scale-95 transition"
                      >
                        <Star
                          size={starSize}
                          weight="fill"
                          className="text-blue-400"
                        />
                      </button>
                    ))}

                    {extra > 0 && (
                      <span className="text-[10px] leading-none px-1 py-0.5 rounded-full bg-blue-50 text-blue-700 self-center justify-self-start">
                        +{extra}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <ActivityLightbox
        key={lb.id || "none"}
        open={lb.open}
        onClose={() => setLb({ open: false, activity: null, id: null })}
        activity={null}
        activityId={lb.id}
        currentUser={currentUser}
      />
    </div>
  );
}